import type { DefaultTheme as Theme } from 'styled-components';
import { themeBasePath } from '@makemydeal/dr-sales-checkin-types';

// Helper function to get PR number from session storage (matches the pattern used in index.html)
const getQueryParamValueFromSessionStorage = (name: string): string | null => {
    const key = 'cmd_oid_' + name.toLowerCase().trim();
    return window.sessionStorage.getItem(key);
};

// Helper function to extract PR number from script tags or URL
const getPrNumberFromEnvironment = (): string | null => {
    // First try session storage
    const prFromSession = getQueryParamValueFromSessionStorage('pr');
    if (prFromSession) {
        return prFromSession;
    }

    // Try to extract from URL query parameters
    const urlParams = new URLSearchParams(window.location.search);
    const prFromUrl = urlParams.get('pr');
    if (prFromUrl) {
        return prFromUrl;
    }

    return null;
};

export const buildThemeRelativeUrl = (version: string | undefined, themeFolder: string, fileName: string): string => {
    const versionSegment = version ? `${version}/` : '';
    return `${themeBasePath}${versionSegment}css/${themeFolder}/${fileName}`;
};

export const addThemeFileLinkToHead = (version: string | undefined, theme: string): void => {
    const headElt = document.querySelectorAll('head').item(0);
    if (!headElt) return;

    const link = document.createElement('link');
    const themeFolder = buildThemeFolderName(theme);
    const themeCssFileName = buildThemeCssFileName(theme);
    let href = buildThemeRelativeUrl(version, themeFolder, themeCssFileName);

    // Check if we're in a PR environment and append PR number if available
    const prNumber = getPrNumberFromEnvironment();
    if (prNumber) {
        href += `?pr=${prNumber}`;
    }

    link.setAttribute('href', href);
    link.setAttribute('rel', 'stylesheet');
    link.setAttribute('type', 'text/css');
    link.setAttribute('crossorigin', 'anonymous');
    headElt.appendChild(link);
};

export const buildThemeFolderName = (theme: string): string => theme;

export const buildThemeCssFileName = (theme: string): string => `theme-${theme}-main.css`;

export const buildThemeJsonFileName = (theme: string): string => `theme-${theme}.json`;

export const getThemeModule = async (theme: string): Promise<Theme> => {
    const version = undefined; // TODO: In future we can support loading multiple versions (maybe for PR environments?)
    addThemeFileLinkToHead(version, theme);
    let themeUrl = buildThemeRelativeUrl(version, theme, buildThemeJsonFileName(theme));

    // Check if we're in a PR environment and append PR number if available
    const prNumber = getPrNumberFromEnvironment();
    if (prNumber) {
        themeUrl += `?pr=${prNumber}`;
    }

    const response = await fetch(themeUrl);
    if (!response.ok) {
        throw new Error('A non-200 response was received when pulling json theme file');
    }
    return response.json();
};

export const getToken = (theme: any, tokenPath: string, defaultValue: string): string => {
    return theme?.tokens?.[tokenPath] || defaultValue;
};
